import {
	type OrderDetailResponseDto,
	type OrderEmailNotificationResponseDto,
	OrderTypeNotification,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { useOrderEmailNotificationSend } from "Order/Api/Mutation/sendOrderNotificationEmailMutation";
import { getOrderDetailQueryKey } from "Order/Api/Query/useOrderDetailQuery";
import { usePartnerReviewByOrderIdQuery } from "Order/Api/Query/usePartnerReviewByOrderIdQuery";
import { OrderReviewClientDetail } from "Order/Widgets/OrderReviewClientDetail";
import { OrderReviewsListWidget } from "Order/Widgets/OrderReviewsListWidget";
import { OrderReviewWidget } from "Order/Widgets/OrderReviewWidget";
import { Loading } from "Shared/Components/Loading/Loading";
import Send from "@mui/icons-material/Send";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type FC, useCallback } from "react";

const RESTRICT_EMAIL_NOTIFICATION_MINUTES = 5;

type OrderReviewTabProps = {
	order: OrderDetailResponseDto;
};

const { partnerReviewShared } = Resources.base.order;

export const OrderReviewTab: FC<OrderReviewTabProps> = ({ order }) => {
	const { t } = useResource();
	const { formatDateTime } = useDateFormat();
	const queryClient = useQueryClient();

	const { data: reviews, isLoading: isLoadingReview } =
		usePartnerReviewByOrderIdQuery(order.id);

	const { mutate: sendOrderEmailNotification } =
		useOrderEmailNotificationSend();

	const handleSendEmailNotification = useCallback(() => {
		sendOrderEmailNotification(
			{
				data: { type: OrderTypeNotification.review },
				orderId: order.id,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: getOrderDetailQueryKey(order.id),
					});
				},
			},
		);
	}, [sendOrderEmailNotification, order.id]);

	const hasRecentReviewEmailNotification = (): boolean => {
		return order.emailNotifications.some((notification) => {
			return (
				notification.type === OrderTypeNotification.review &&
				dayjs().diff(dayjs(notification.dateSent), "minute") <=
					RESTRICT_EMAIL_NOTIFICATION_MINUTES
			);
		});
	};

	const timeLastSentEmail = order.emailNotifications
		.filter(
			(notification) => notification.type === OrderTypeNotification.review,
		)
		.reduce<OrderEmailNotificationResponseDto | null>((latest, current) => {
			if (!latest) return current;
			return dayjs(current.dateSent).isAfter(dayjs(latest.dateSent))
				? current
				: latest;
		}, null)?.dateSent;

	return isLoadingReview ? (
		<Stack
			direction="column"
			justifyContent="center"
			alignItems="center"
			spacing={2}
			sx={{ height: "100%" }}
		>
			<Loading />
		</Stack>
	) : (
		<Stack gap={1}>
			<Stack gap={2} mb={2}>
				<Box>
					<Button
						startIcon={<Send />}
						onClick={handleSendEmailNotification}
						disabled={hasRecentReviewEmailNotification()}
					>
						{t(partnerReviewShared.askForReview)}
					</Button>
				</Box>
				{timeLastSentEmail && (
					<Typography variant="caption" color="text.secondary">
						Email was last sent {formatDateTime(timeLastSentEmail)}
					</Typography>
				)}
			</Stack>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, md: 7 }}>
					<OrderReviewWidget order={order} reviews={reviews} />
				</Grid2>
				<Grid2 size={{ xs: 12, md: 5 }}>
					<Stack spacing={2}>
						<OrderReviewClientDetail order={order} />
						{(reviews ?? []).length > 0 && (
							<OrderReviewsListWidget order={order} reviews={reviews ?? []} />
						)}
					</Stack>
				</Grid2>
			</Grid2>
		</Stack>
	);
};
